import { createSlice } from '@reduxjs/toolkit';

/**
 * Partner slice — Partner Portal Phase 1, Part B (dashboard).
 *
 * Consumes the dark /v5/:orgID/partner surface (behind the `partnerPortal` feature flag):
 *   - GET  /partner/context           → the signed-in user's partner identity + org-scoped grant summary
 *   - GET  /partner/grants            → the org's installer-grant roster (customer-admin view)
 *   - POST /partner/grants            → issue a scoped installer device grant
 *   - POST /partner/grants/:id/revoke → revoke (idempotent)
 *   - POST /partner/grants/:id/renew  → extend expiry (default +90d server-side)
 *
 * ⚠️ Callers MUST gate every dispatch behind `useFeature('partnerPortal', false)` — no /partner
 * request should fire until the flag resolves true (the surface 403s otherwise, and the whole point
 * is that it stays invisible while dark).
 *
 * The camera-grant lineage is conceptual (this mirrors backend `cameraAccess` VIEW grants); the slice
 * *structure* follows `bookableResources` because grant management is a create/revoke/renew CRUD
 * lifecycle with a one-shot "close the modal" signal, which cameras does not have.
 */
const initialState = {
	// read: partner context blob, org-scoped (context[orgID])
	context: {}, // context[orgID] = { partner, resourcesAvailable, activeGrants, grantSummary, selectedOrgRole }
	contextLoading: false,
	contextLoadingComplete: false,
	// GET /partner/context 403s for a read-only caller (requireNotReadOnly sits on the whole partner
	// router as-built). We surface that as a distinct "forbidden" state so the shell degrades quietly
	// (hide the surface) instead of showing an error — see Part B correction #3.
	contextForbidden: false,

	// USER-scoped partner identity, resolved once per session from the ORG-LESS
	// /v5/partner-portal/overview. Distinct from `context` above, which is per-org and lives behind the
	// per-org partnerPortal flag: identity answers "is this person a partner at all", which no per-org
	// flag can gate. null = not a partner (also the fail-closed value for every error).
	identity: null, // { partner, orgs: [{ orgID, orgName, hasActiveGrant, ... }] }
	identityLoading: false,
	identityChecked: false,

	// grant roster, org-scoped (grants[orgID] = [...rosterRows])
	grants: {},
	grantsLoading: false,
	grantsLoadingComplete: false,

	// grant mutations — one loading flag covers create/revoke/renew (never distinguished in the UI)
	mutationLoading: false,
	// one-shot success signal so the grant modal can close on mutation success
	mutationComplete: false,

	error: null
};

const partnerSlice = createSlice({
	name: 'partner',
	initialState,
	reducers: {
		// 🔥 Partner context (identity + grant summary for the selected org)
		loadPartnerContext: (state) => {
			state.contextLoading = true;
			state.contextForbidden = false;
			state.error = null;
		},
		loadPartnerContextSuccess: (state, action) => {
			state.contextLoading = false;
			state.contextLoadingComplete = true;
			state.context[action.payload.orgID] = action.payload.data;
		},
		// 403 → not an error; the caller lacks read access (read-only role, or flag off). Degrade quietly.
		loadPartnerContextForbidden: (state, action) => {
			state.contextLoading = false;
			state.contextLoadingComplete = true;
			state.contextForbidden = true;
			state.context[action.payload.orgID] = null;
		},
		loadPartnerContextError: (state, action) => {
			state.contextLoading = false;
			state.error = action.payload;
		},

		// 🔥 USER-scoped partner identity (org-less). Deliberately has no `error` branch: not being a
		// partner is the normal outcome for almost every user, and a failure to resolve is treated the
		// same as "not a partner" — the tile just does not appear. Nothing here should ever raise a toast.
		loadPartnerIdentity: (state) => {
			state.identityLoading = true;
		},
		loadPartnerIdentityResolved: (state, action) => {
			state.identityLoading = false;
			state.identityChecked = true;
			state.identity = action.payload || null;
		},

		// 🔥 Grant roster
		loadGrants: (state) => {
			state.grantsLoading = true;
			state.error = null;
		},
		loadGrantsSuccess: (state, action) => {
			state.grantsLoading = false;
			state.grantsLoadingComplete = true;
			// Full replace of the org bucket — a roster reload returns the authoritative current set,
			// so a revoked/renewed row reflects immediately without stale-merge risk.
			state.grants[action.payload.orgID] = action.payload.data || [];
		},
		loadGrantsError: (state, action) => {
			state.grantsLoading = false;
			state.error = action.payload;
		},

		// 🔥 Create (issue) a grant
		createGrant: (state) => {
			state.mutationLoading = true;
			state.mutationComplete = false;
			state.error = null;
		},
		createGrantSuccess: (state) => {
			state.mutationLoading = false;
			state.mutationComplete = true;
		},
		createGrantError: (state, action) => {
			state.mutationLoading = false;
			state.error = action.payload;
		},

		// 🔥 Revoke
		revokeGrant: (state) => {
			state.mutationLoading = true;
			state.mutationComplete = false;
			state.error = null;
		},
		revokeGrantSuccess: (state) => {
			state.mutationLoading = false;
			state.mutationComplete = true;
		},
		revokeGrantError: (state, action) => {
			state.mutationLoading = false;
			state.error = action.payload;
		},

		// 🔥 Renew (extend expiry)
		renewGrant: (state) => {
			state.mutationLoading = true;
			state.mutationComplete = false;
			state.error = null;
		},
		renewGrantSuccess: (state) => {
			state.mutationLoading = false;
			state.mutationComplete = true;
		},
		renewGrantError: (state, action) => {
			state.mutationLoading = false;
			state.error = action.payload;
		},

		resetGrantMutationState: (state) => {
			state.mutationComplete = false;
			state.error = null;
		}
	},
	extraReducers: (builder) => {
		builder.addCase('auth/logout', () => initialState);
	}
});

export const partnerActions = partnerSlice.actions;
export default partnerSlice.reducer;
